"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuthStore } from "@/stores/auth-store";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { getRedirectUrl, clearRedirectUrl } from "@/lib/utils/redirect-storage";
import {
  alignServerAuthCookiesBeforeAppEntry,
  clearClientAuthIfServerSaysSignedOut,
  getAuthShellDebug,
  logAuthDecision,
} from "@/lib/auth/verify-web-cookie-session";

/**
 * Routes that show logged-out UI (landing, login, signup).
 * Logged-in users should NEVER see these - redirect to dashboard immediately.
 * We show a loading screen until auth state is resolved to prevent any flash.
 */
const LOGGED_OUT_ROUTES = ["/", "/auth/login", "/auth/signup", "/auth", "/auth/forgot-password"];

/**
 * Auth routes that handle their own flows - don't gate these.
 * e.g. OAuth callback, password reset link
 */
const UNGATED_AUTH_ROUTES = ["/auth/callback", "/auth/set-password", "/auth/add-phone"];

function isLoggedOutRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  if (UNGATED_AUTH_ROUTES.some((r) => pathname.startsWith(r))) return false;
  return LOGGED_OUT_ROUTES.includes(pathname) || pathname === "/";
}

/**
 * AuthGate - Prevents flash of logged-out UI for returning users.
 *
 * When a logged-in user reopens the app, they may land on / or /auth/login.
 * Without this gate, they'd briefly see the landing page or login form before
 * redirect. Social apps (Instagram, Facebook, Hinge) never show that - they
 * go straight to the app.
 *
 * This component shows a loading screen until we've determined auth state,
 * then either redirects logged-in users to dashboard or renders children
 * for genuinely logged-out users.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isAuthenticated, syncSession, _hasHydrated } = useAuthStore();
  const { isOnline } = useNetworkStatus();
  const [authResolved, setAuthResolved] = useState(false);
  const redirectingRef = useRef(false);
  const checkInFlightRef = useRef<Promise<void> | null>(null);
  const hardRedirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isGatedRoute = isLoggedOutRoute(pathname);

  useEffect(() => {
    const redirectAuthenticatedUser = () => {
      const currentPath = pathname || "/";
      const requestedTarget = getRedirectUrl("/dashboard");
      const safeTarget = isLoggedOutRoute(requestedTarget) ? "/dashboard" : requestedTarget;
      const redirectTo = safeTarget === currentPath ? "/dashboard" : safeTarget;

      clearRedirectUrl();
      redirectingRef.current = true;
      router.replace(redirectTo);

      // Safety net: if Next router doesn't navigate, hard-redirect to avoid infinite loading.
      if (hardRedirectTimerRef.current) {
        clearTimeout(hardRedirectTimerRef.current);
      }
      hardRedirectTimerRef.current = setTimeout(() => {
        if (typeof window !== "undefined" && window.location.pathname === currentPath) {
          window.location.replace("/dashboard");
        }
      }, 1200);
    };

    if (!isGatedRoute) {
      redirectingRef.current = false;
      if (hardRedirectTimerRef.current) {
        clearTimeout(hardRedirectTimerRef.current);
        hardRedirectTimerRef.current = null;
      }
      setAuthResolved(true);
      return;
    }

    setAuthResolved(false);
    let mounted = true;
    let unsubscribe: (() => void) | null = null;

    const checkAndResolve = async () => {
      if (!mounted) return;

      // 2. Offline: use persisted state only, no network check
      if (!isOnline) {
        const state = useAuthStore.getState();
        if (state.user && state.isAuthenticated) {
          redirectAuthenticatedUser();
        }
        setAuthResolved(true);
        return;
      }

      // 3. Persisted auth: middleware uses HTTP cookies — must match before client navigates to /dashboard (native + web).
      const persistedState = useAuthStore.getState();
      const hadPersistedAuth = !!(persistedState.user && persistedState.isAuthenticated);

      if (hadPersistedAuth) {
        logAuthDecision("AuthGate: persisted auth, align session + cookie-session probe (middleware parity)", {
          ...getAuthShellDebug(),
          userId: persistedState.user?.id?.slice(0, 8),
        });
        const cookieResult = await alignServerAuthCookiesBeforeAppEntry();
        if (cookieResult === "ok") {
          logAuthDecision("AuthGate: redirect allowed, cookie-session OK (matches middleware)", {
            ...getAuthShellDebug(),
            cookieResult,
          });
          redirectAuthenticatedUser();
          setAuthResolved(true);
          return;
        }
        logAuthDecision("AuthGate: cookie-session not OK, so not entering the app", {
          ...getAuthShellDebug(),
          cookieResult,
        });
        await clearClientAuthIfServerSaysSignedOut(cookieResult, "cookie_session_not_ok");
        setAuthResolved(true);
        return;
      }

      // 4. No persisted auth - do a quick session sync for mobile (Supabase/Capacitor may have session)
      // CRITICAL: Use short bounded timeouts - sessionRestorePromise can hang on Android
      const isMobileApp =
        typeof window !== "undefined" &&
        /(iPhone|iPad|iPod|Android)/i.test(navigator.userAgent) &&
        /Capacitor/i.test(navigator.userAgent);

      if (isMobileApp) {
        try {
          const { sessionRestorePromise } = await import("@/lib/supabase/client");
          await Promise.race([
            sessionRestorePromise,
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), 2000)
            ),
          ]).catch(() => {});
          await Promise.race([
            syncSession(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), 1500)
            ),
          ]).catch(() => {});

          const state = useAuthStore.getState();
          if (state.isAuthenticated && state.user && mounted) {
            const cookieResult = await alignServerAuthCookiesBeforeAppEntry();
            if (cookieResult === "ok") {
              redirectAuthenticatedUser();
            } else {
              await clearClientAuthIfServerSaysSignedOut(cookieResult, "auth_gate_mobile_sync_cookie_mismatch");
            }
          }
        } catch {
          // Silent - show logged-out UI
        }
      }

      if (mounted) {
        setAuthResolved(true);
      }
    };

    // Safety: never block longer than 2s - prevents infinite loading on Android/fresh install
    const safetyTimeout = setTimeout(() => {
      if (mounted) {
        setAuthResolved(true);
      }
    }, 2000);

    // 1. Wait for Zustand persist to rehydrate (so we have accurate persisted auth)
    const runCheckAndResolve = async () => {
      if (checkInFlightRef.current) {
        await checkInFlightRef.current;
        return;
      }
      const task = checkAndResolve().finally(() => {
        checkInFlightRef.current = null;
      });
      checkInFlightRef.current = task;
      await task;
    };

    if (!_hasHydrated) {
      unsubscribe = useAuthStore.subscribe((state) => {
        if (state._hasHydrated && mounted) {
          unsubscribe?.();
          unsubscribe = null;
          runCheckAndResolve();
        }
      });
      if (useAuthStore.getState()._hasHydrated) {
        unsubscribe?.();
        unsubscribe = null;
        runCheckAndResolve();
      } else {
        // Fallback: if _hasHydrated doesn't fire within 100ms, proceed with current state
        const hydrationFallback = setTimeout(() => {
          if (mounted && !useAuthStore.getState()._hasHydrated) {
            unsubscribe?.();
            unsubscribe = null;
            runCheckAndResolve();
          }
        }, 100);
        return () => {
          mounted = false;
          clearTimeout(safetyTimeout);
          clearTimeout(hydrationFallback);
          unsubscribe?.();
        };
      }
    } else {
      runCheckAndResolve();
    }

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      if (hardRedirectTimerRef.current) {
        clearTimeout(hardRedirectTimerRef.current);
        hardRedirectTimerRef.current = null;
      }
      unsubscribe?.();
    };
  }, [pathname, isGatedRoute, _hasHydrated, isOnline, router, syncSession]);

  // Redirect when auth state updates after we've resolved (e.g. persist hydrated late, SessionSync)
  useEffect(() => {
    if (
      !isGatedRoute ||
      !authResolved ||
      !isAuthenticated ||
      !user ||
      redirectingRef.current ||
      typeof window === "undefined"
    ) {
      return;
    }

    let cancelled = false;

    const runLateRedirect = async () => {
      logAuthDecision("AuthGate late hydrate: verifying cookie session before redirect", {
        ...getAuthShellDebug(),
      });
      const cookieResult = await alignServerAuthCookiesBeforeAppEntry();
      if (cancelled) return;
      if (cookieResult !== "ok") {
        logAuthDecision("AuthGate late hydrate: cookie check not OK, so not entering the app", {
          ...getAuthShellDebug(),
          cookieResult,
        });
        await clearClientAuthIfServerSaysSignedOut(cookieResult, "auth_gate_late_hydrate");
        return;
      }
      logAuthDecision("AuthGate late hydrate: redirect allowed, cookie-session OK", {
        ...getAuthShellDebug(),
        cookieResult,
      });
      const requestedTarget = getRedirectUrl("/dashboard");
      const safeTarget = isLoggedOutRoute(requestedTarget) ? "/dashboard" : requestedTarget;
      const redirectTo = safeTarget === (pathname || "/") ? "/dashboard" : safeTarget;
      clearRedirectUrl();
      redirectingRef.current = true;
      router.replace(redirectTo);
    };

    void runLateRedirect();
    return () => {
      cancelled = true;
    };
  }, [isGatedRoute, authResolved, isAuthenticated, user, router, pathname]);

  // Skip full-screen splash on `/` so the landing page can paint immediately; other gated routes
  // still wait to avoid logged-out UI flash for returning sessions.
  const blockUntilAuthResolved =
    isGatedRoute && !authResolved && pathname !== "/";
  if (blockUntilAuthResolved) {
    return <LoadingScreen />;
  }

  // For gated routes: if we resolved and user is logged in, they're being redirected - show loading
  if (isGatedRoute && authResolved && isAuthenticated && user) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
}
